import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { DashboardsViews } from './utils/types'

import {
  getUniqueDashboardName,
  getUniqueDashboardSlug,
  slugFromName,
} from '@/views/Dashboards/components/utils/helpers'
import { cloneDashboardWidgets } from '@/views/Dashboards/utils/clone'

import api from '@/store/api'

import { useResponsive } from '@/plugins/responsiveUI'
import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'
import runDashboardsSettingsMigrations from './utils/helpers'

import { useRepositoriesStore } from '@/store/repositories/index'

const DASHBOARDS_VIEWS_VERSION = 'dashboardsViewsVersion'

export const useDashboardsStore = defineStore('dashboards', () => {
  // INIT
  const { isMobile, isTablet } = useResponsive()

  const repositoriesStore = useRepositoriesStore()

  let abortController = new AbortController()

  const list = ref<DashboardsViews[]>([])
  const initFlag = ref(false)

  const isEditMode = ref(false)
  const isAIEnabled = ref(false)

  const isMobileMode = computed(() => isMobile.value || isTablet.value)

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS

  const getEditMode = computed(() => isEditMode.value)
  const getAIEnabled = computed(() => isAIEnabled.value)

  const currentDashboardsType = computed(() =>
    isMobileMode.value ? 'mobile' : 'desktop',
  )

  const getList = computed(() => {
    if (!repositoryId.value) return
    return list.value.filter(item => item.type === currentDashboardsType.value)
  })

  // SETTERS

  const setList = (value: DashboardsViews[]) => {
    list.value = value
  }

  const setEditMode = (value: boolean) => {
    isEditMode.value = value
  }

  const setAIEnabled = (value: boolean) => {
    isAIEnabled.value = value
  }

  // ACTIONS
  const fetch = async () => {
    try {
      const result = await api.get<DashboardsViews[]>('dashboard_views', {
        params: {
          repository_id: repositoryId.value,
        },
        signal: abortController.signal,
      })
      initFlag.value = true
      setList(result?.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
    await runViewsMigration()
  }

  const store = async (data: DashboardsViews) => {
    try {
      const result = await api.post<DashboardsViews[]>('dashboard_views', {
        data: [
          {
            ...data,
            type: currentDashboardsType.value,
            repository_id: repositoryId.value,
          },
        ],
        repository_id: repositoryId.value,
      })
      result.data.forEach(item => {
        addStoreListItem<DashboardsViews>(list.value, item)
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const cloneView = async (source: DashboardsViews) => {
    const views = getList.value
    const name = getUniqueDashboardName(source.name, views)
    const slug = getUniqueDashboardSlug(name, views, slugFromName(name))
    const widgets = cloneDashboardWidgets(source.widgets)
    const [created] = await store({
      id: '',
      name,
      slug,
      widgets,
    })
    if (!created) throw Error('Cloned dashboard view is not available')
    return created
  }

  const update = async (
    id: string,
    data: DashboardsViews,
    callback?: () => void,
  ) => {
    try {
      const result = await api.put<DashboardsViews>(`dashboard_views/${id}`, {
        ...data,
        repository_id: repositoryId.value,
      })
      updateStoreListItem<DashboardsViews>(list.value, result.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      callback?.()
    }
  }

  const remove = async (id: string) => {
    try {
      await api.delete(
        `dashboard_views/${id}?repository_id=${repositoryId.value}`,
      )
      removeStoreListItem<DashboardsViews>(list.value, id)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const runViewsMigration = async () => {
    if (!list.value.length) return
    const version =
      repositoriesStore.getCurrentUISettings?.[DASHBOARDS_VIEWS_VERSION] ||
      '19700101'
    const migration = await runDashboardsSettingsMigrations(list.value, version)
    if (!migration) return
    await Promise.all(
      migration.migratedViews.map(item => update(item.id, item)),
    )
    repositoriesStore.setCurrentUISettings([
      [DASHBOARDS_VIEWS_VERSION, migration.version],
    ])
  }

  const clear = () => {
    isEditMode.value = false
  }

  watch(
    repositoryId,
    value => {
      if (!value) return
      fetch()
    },
    { immediate: true },
  )

  return {
    initFlag,

    getEditMode,
    setEditMode,

    getAIEnabled,
    setAIEnabled,

    getList,

    store,
    cloneView,
    update,
    remove,

    clear,
  }
})
